import { createGlobalStyle, styled, keyframes } from "styled-components";
import * as AlfredoInterfaces from "../../interfaces";
import { raizEscala } from "./escala";
import { raizDeCores } from "./tokens";
import { ate } from "./breakpoints";

const GlobalStyles = createGlobalStyle`
/*
  0. A escala fluida (--u e as escadas --fs-* / --sp-*). Fica aqui dentro, e não
     num segundo createGlobalStyle, para não existir pergunta de ordem de
     injeção: onde há GlobalStyles, há a unidade. Ver escala.ts.
*/
${raizEscala}
/*
  0.1 Os tokens de cor do sistema de design ("a tela de LCD"). Mesma razão de
      morarem aqui dentro: um estilo global só, sem ordem de injeção. Os temas
      do styled-components mapeiam PAPÉIS sobre estes valores; as variáveis
      existem para o que não passa pelo ThemeProvider (o pino do mapa, por
      exemplo, que é um elemento DOM criado à mão). Ver tokens.ts.
*/
${raizDeCores}
/*
  0.2 O FOCO VISÍVEL. Uma regra, e ela cobre o app inteiro.

  Levantado antes de escrever: focus-visible aparecia em DOIS arquivos da
  arvore (os dois recentes), e outline em 56 lugares — quase todos para
  DESLIGAR (outline: none no hover ou no focus de campo). O resultado e que
  quem navega por teclado não enxerga onde está: o Tab anda e nada muda na tela.

  :focus-visible (e nao :focus) e o seletor certo: o navegador só o aplica
  quando o foco veio do TECLADO. Clique de mouse não passa a mostrar anel — que
  e a razao de as pessoas terem comecado a escrever outline:none no comeco
  desta historia.

  O outline-offset afasta o anel da borda para ele não se confundir com a borda
  do próprio controle, e o anel usa a cor de AÇÃO do tema, que já é a que passa
  em contraste nos dois temas.
*/
:where(a, button, input, select, textarea, summary, [tabindex]):focus-visible {
  outline: 3px solid ${({ theme }) => theme.colors.selection};
  outline-offset: 2px;
  border-radius: 4px;
}

/*
  0.3 QUEM PEDIU MENOS MOVIMENTO, RECEBE MENOS MOVIMENTO.

  A regra 1 abaixo aplica transition:all 0.1s em TODO elemento da pagina.
  Para quem marcou "reduzir movimento" no sistema (vestibular, enxaqueca), isso
  é exatamente o que o ajuste existe para evitar.
*/
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/*
  1. Use a more-intuitive box-sizing model.
*/
*, *::before, *::after {
  box-sizing: border-box;
  transition: all 0.1s;
}
/*
  2. Remove default margin
*/
* {
  margin: 0;
}
/*
  Typographic tweaks!
  3. Add accessible line-height
  4. Improve text rendering
*/
body {
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  font: 400 1rem Poppins, sans-serif;
  /*
    O corpo é a base que todo texto sem font-size próprio herda — é a troca de
    maior alcance da escala. Vem DEPOIS do atalho \`font\` porque o atalho
    reescreve font-size (e é ele, não o \`line-height: 1.5\` acima, que decide a
    entrelinha do corpo hoje: o atalho zera line-height para \`normal\`. Esse
    comportamento é o de hoje e está preservado de propósito).
    Não se troca o 1rem dentro do atalho: \`font\` com var() vira valor de
    substituição pendente e, se a variável faltasse, o atalho INTEIRO cairia.
  */
  font-size: var(--fs-base);
  /*
    O FUNDO DA PÁGINA não é o mesmo da superfície.
    No escuro a pagina e --tela-fundo (#121212) e o cartao e --tela
    (#252525) — e essa diferenca que dá profundidade e faz o cartão existir. No
    claro a página é o papel tingido. Ver seção 5.1 do sistema de design.
  */
  background-color: ${({ theme }) =>
    theme.title === "dark" ? "var(--tela-fundo)" : theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
}
/*
  5. Improve media defaults
*/
img, picture, video, canvas, svg {
  display: block;
  max-width: 100%;
}
/*
  6. Remove built-in form typography styles
*/
input, button, textarea, select {
  font: 400 1rem Poppins, sans-serif;
  /* Campo e botão não herdam fonte do corpo: precisam da escala em separado. */
  font-size: var(--fs-base);
}
/*
  7. Avoid text overflows
*/
p, h1, h2, h3, h4, h5, h6 {
  overflow-wrap: break-word;
}
/*
  8. Create a root stacking context
*/
#root, #__next {
  isolation: isolate;
}
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const expandLeftAnimation = keyframes`
  0% {
    flex: 0.1;
  }
  100% {
    flex: 1;
  }
`;

/*
 * O CARTÃO BRANCO QUE ENVOLVE CADA TELA.
 *
 * Tinha `height: 100%; max-height: 100%; overflow: hidden`. Como quem rola é o
 * ContentWrapper acima dele, isso significava: a tela tem exatamente a altura
 * da janela e o que passar disso é CORTADO, sem rolagem nenhuma. Efeito
 * medido: em Relatórios > Ordens, a tabela sumia no meio e a paginação — os
 * botões de próxima página, o seletor de itens por página, a contagem — ficava
 * fora de alcance. Não havia como chegar na segunda página.
 *
 * `min-height: 100%` no lugar de `height`: o cartão continua ocupando a tela
 * inteira quando o conteúdo é curto (que é o efeito visual que se queria) e
 * CRESCE quando o conteúdo é longo, deixando o ContentWrapper rolar.
 */
export const Container = styled.div`
  position: relative;
  /* Era rgba(255,255,255,0.649) fixo: no tema escuro o cartao da pagina
     continuava branco-leitoso, e todo texto do tema (claro) ficava por cima
     dele — foi o que a medição de contraste pegou. */
  background: ${(props) => props.theme.colors.background};
  backdrop-filter: blur(10px);
  padding: var(--sp-20);
  box-sizing: border-box;
  border: 2px solid rgba(224, 224, 224, 0.5);
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 100%;
  display: flex;
  flex-direction: column;
  /* Nada de overflow: hidden. O que precisa rolar de lado (a tabela) tem o
     próprio container de rolagem; cortar aqui escondia a rolagem dele também. */
  overflow: visible;

  ${ate.tablet} {
    padding: var(--sp-12);
    border-radius: 14px;
  }
`;
export const StyledImage = styled.img`
  position: absolute;
  height: 50%;
  top: 50%;
  left: 35%;
  z-index: -1;
`;
/*
  A Home põe dois cartões LADO A LADO (a rede e "últimas opções"). Com as duas
  barras abertas em 1440 sobram ~740 de conteúdo, e cada cartão fica com ~360 —
  é por isso que o nome da loja quebrava em três linhas e as métricas viravam
  uma coluna, numa janela que "era grande".

  Grade com auto-fit em vez de flex + @media: a quebra passa a depender da
  largura DISPONÍVEL, não da janela. Dois cartões enquanto couberem 420 para
  cada um; um só abaixo disso — inclusive em 1440 com as duas barras abertas,
  que é o caso que nenhum @media de janela pegaria.
*/
export const FlexContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 420), 1fr));
  gap: var(--sp-16);
  align-content: start;
  flex: 1;
`;
export const FixedContainer = styled.div`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 10;
`;
export const FlexItem = styled.div`
  flex: 1;
  /* Sem isto o item se recusa a encolher abaixo do conteúdo e a Home inteira
     ganha rolagem horizontal em tela estreita. */
  min-width: 0;
`;
export const SearchContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--sp-20);
  padding: var(--sp-20);
  background-color: #f0f0f0;
  border-radius: 20px;
`;
export const TableWrapper = styled.div<AlfredoInterfaces.TableWrapperProps>`
  padding: var(--sp-10);
  opacity: ${(props) => props.componentBrightness || "1"};
  visibility: ${(props) => props.componentStatus || "visible"};
  overflow-y: auto;
`;
export const Table = styled.table<AlfredoInterfaces.TableProps>`
  width: 100%;
  height: fit-content;
  background-color: ${(props) => props.componentBackgroundColor || "#ffffff"};
  color: ${(props) => props.componentColor || "#000000"};
  font-size: ${(props) => props.componentFontSize || "14px"};
`;
export const TableHeader = styled.thead<AlfredoInterfaces.TableHeaderProps>`
  background-color: ${(props) => props.componentBackgroundColor || "#f0f0f0"};
  color: ${(props) => props.componentColor || "#000000"};
  height: calc(var(--u) * 50);
`;
export const TableHeaderCell = styled.th<AlfredoInterfaces.TableHeaderCellProps>`
  padding: 0px;
  text-align: center;
  font-weight: bold;
  width: ${(props) => props.columnWidth || "auto"};
  position: sticky;
  top: 0;
  background-color: inherit;
`;
export const TableCellObservation = styled.th<AlfredoInterfaces.TableCellObservationProps>`
  text-align: ${(props) => props.textAlign || "left"};
  font-weight: normal;
  position: sticky;
  top: 0;
`;
export const TableBody = styled.tbody<AlfredoInterfaces.TableBodyProps>`
  color: ${(props) => props.componentColor || "#000000"};
  height: fit-content;
  vertical-align: top;
`;
export const TableRow = styled.tr<AlfredoInterfaces.TableRowProps>`
  height: calc(var(--u) * 20);
  &:nth-child(even) {
    background-color: ${(props) => props.componentBackgroundColor || "#f0f0f0"};
  }
  &:nth-child(odd) {
    background-color: ${(props) => props.componentBackgroundColor || "#ffffff"};
  }
`;
export const TableRowObservation = styled.tr``;
export const TableCell = styled.td<AlfredoInterfaces.TableCellProps>`
  padding: var(--sp-5);
  text-align: center;
  font-weight: bold;
  width: ${(props) => props.columnWidth || "auto"};
`;

export const SignPageContainer = styled.div`
  display: flex;
  height: 100vh;
  background-color: #fee97b;
`;

export const SignPageTitle = styled.h1<{ $isLoading: boolean }>`
  position: sticky;
  font-size: calc(var(--u) * 70);
  z-index: 10;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.selection};
  text-align: center;
  text-shadow: -0.7px -0.7px 0 #000, 0.7px -0.7px 0 #000, -0.7px 0.7px 0 #000,
    1.4px 1.4px 0 #000;
  white-space: normal; /* Permite quebra de linha */
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${(props) =>
    props.$isLoading
      ? "100%"
      : "70%"}; /* Garante que a largura máxima seja 100% */
  opacity: ${(props) =>
    props.$isLoading ? 0 : 1}; /* Inicialmente escondido */
  animation: ${(props) => (props.$isLoading ? fadeIn : "none")} 1s linear 2s
    forwards;
`;

export const Left = styled.div<{ isLoading: boolean }>`
  width: ${(props) => (props.isLoading ? "100%" : "70%")};
  flex: 1;
  background-color: ${(props) =>
    props.isLoading ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.7)"};
  background-size: ${(props) => (props.isLoading ? "contain" : "cover")};
  background-blend-mode: overlay;
  display: flex;
  gap: ${(props) => (props.isLoading ? "2%" : "1%")};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  animation: ${(props) => (props.isLoading ? expandLeftAnimation : "none")} 2s
    forwards;
  transition: all 3s ease-in-out;

  &::before,
  &::after {
    content: "";
    position: absolute;
    width: ${(props) => (props.isLoading ? "2200%" : "220%")};
    height: 120%;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isLoading ? "#ffd90070" : "#ffd90050"};
    left: -50%;
    transform: translateX(-50%);
    transition: all 0.5s ease-in-out;
  }

  &::before {
    top: ${(props) => (props.isLoading ? "-10%" : "0")};
    transform: translateX(-50%)
      translateY(${(props) => (props.isLoading ? "-60%" : "-50%")});
  }

  &::after {
    bottom: ${(props) => (props.isLoading ? "-10%" : "0")};
    transform: translateX(-50%)
      translateY(${(props) => (props.isLoading ? "60%" : "50%")});
  }
`;

export const Right = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

export const Logo = styled.img<{
  $selectedSession: string;
  $isLoading: boolean;
}>`
  width: ${(props) => (props.$isLoading ? "calc(var(--u) * 200)" : "calc(var(--u) * 150)")};
  height: ${(props) => (props.$isLoading ? "calc(var(--u) * 200)" : "calc(var(--u) * 150)")};
  opacity: ${(props) => (props.$isLoading ? 0 : 1)};
  z-index: 1;
  animation: ${(props) => (props.$isLoading ? fadeIn : "none")} 2s linear
    forwards;
  animation-delay: 1s;
`;

export const WelcomeText = styled.div`
  z-index: 1;
  color: #000000;
  font-size: 4em;
  font-weight: bolder;
  text-align: center;
  opacity: 0;
  animation: fadeIn 1s forwards;
`;

export const DescriptionText = styled.div`
  position: absolute;
  bottom: calc(var(--u) * 20);
  z-index: 1;
  color: #aea04a;
  font-size: var(--fs-base);
`;

export const ModalContent = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  padding: var(--sp-20);
  border-radius: 10px;
  width: 85%;
`;

export const SignUp = styled.div`
  position: absolute;
  top: calc(var(--u) * 10);
  right: calc(var(--u) * 20);
  justify-content: flex-start;
  display: flex;
  align-items: center;
`;

export const SignUpText = styled.span`
  color: rgba(0, 0, 0, 0.5);
  margin-right: var(--sp-10);
`;

export const SignUpButton = styled.button`
  background: transparent;
  border: 2px solid rgba(0, 0, 0, 0.5);
  border-radius: 15px;
  padding: var(--sp-8) var(--sp-10);
  cursor: pointer;
  transition: 0.3s;
  color: rgba(0, 0, 0, 0.5);
  font-size: var(--fs-xs);
  display: inline-flex;
  align-items: center;

  .material-icon {
    font-family: "Material Icons";
    font-style: normal;
    font-size: var(--fs-2xl);
    margin-right: calc(var(--u) * 3);
  }

  &:hover {
    color: rgba(0, 0, 0, 0.5);
    background: rgba(255, 196, 0, 0.372);
  }
`;

export default GlobalStyles;
